import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/constants";
import { CalendarClock, Loader2, AlertTriangle, Clock, MessageSquarePlus, EyeOff } from "lucide-react";

// ITEM 24 — the weekly conversation agenda. A running order for a fixed-length meeting:
// decisions that need a person first, things waiting on a client next, and honest about
// what it left out. The outcome returns through the ONE Capture box, in the person's words.

const TYPE_LABEL = {
  stuck_review: "Stuck — is this real?",
  reply_review: "Reply overdue",
  payment_review: "Payment overdue",
  silent_milestone: "Milestone outcome missing",
  reply: "Awaiting reply",
  payment: "Awaiting payment",
};

const openCapture = (clientId, seedText) => {
  window.dispatchEvent(new CustomEvent("rex:open-capture", { detail: { clientId, seedText } }));
};

function AgendaCard({ item, index, tone }) {
  const waited = item.waited_days != null ? `${item.waited_days}d` : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`agenda-item-${item.section}-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${tone.badge}`}>
            {index + 1}
          </span>
          <div>
            {item.client_id ? (
              <Link to={`/clients/${item.client_id}`} className="text-base font-semibold text-slate-900 hover:underline" data-testid="agenda-client-link">
                {item.headline}
              </Link>
            ) : (
              <span className="text-base font-semibold text-slate-900">{item.headline}</span>
            )}
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-medium ${tone.tag}`}>{TYPE_LABEL[item.type] || item.type}</span>
              {waited && <span className="inline-flex items-center gap-1 text-slate-400"><Clock className="h-3 w-3" />{waited}</span>}
              {item.review_date && <span className="text-slate-400">review {item.review_date}</span>}
            </div>
          </div>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-600">{item.why}</p>
      <div className="mt-3 flex items-center">
        <button
          onClick={() => openCapture(item.client_id, item.capture_prompt)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          data-testid="agenda-capture-btn">
          <MessageSquarePlus className="h-3.5 w-3.5" /> Capture outcome
        </button>
      </div>
    </div>
  );
}

function LeftOut({ label, data }) {
  if (!data || data.hidden <= 0) return null;
  const parts = Object.entries(data.by_type || {}).map(([t, n]) => `${n} ${TYPE_LABEL[t] || t}`);
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid={`agenda-leftout-${label}`}>
      <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        <span className="font-medium text-slate-700">{data.hidden} more left off this agenda</span> to keep the meeting to time
        {parts.length > 0 && <> — {parts.join(", ")}</>}. Showing {data.shown} of {data.total}.
      </span>
    </div>
  );
}

export default function WeeklyAgenda() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState(10); // the meeting's fixed length — the running order fits this

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/relationship/weekly-agenda?slots=${slots}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slots]);

  const nothing = data && data.decisions.length === 0 && data.awaiting_client.length === 0;

  return (
    <div className="mx-auto max-w-4xl px-6 py-8" data-testid="weekly-agenda-page">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-violet-50 p-2 text-violet-600"><CalendarClock className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">This week's agenda</h1>
            <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-500">
              Meeting length
              <select
                value={slots}
                onChange={(e) => setSlots(Number(e.target.value))}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
                data-testid="agenda-slots-select">
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value={20}>Top 20</option>
              </select>
            </label>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            The running order for your weekly conversation — decisions that need a person first,
            things waiting on a client next. It's honest about what it left off to keep to time.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-sm text-slate-400" data-testid="agenda-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Building the running order…
        </div>
      )}

      {!loading && data && (
        <>
          {nothing ? (
            <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center" data-testid="agenda-empty">
              <p className="text-sm font-medium text-slate-700">Nothing needs the meeting this week.</p>
              <p className="mt-1 text-sm text-slate-500">No decisions are due and nothing is sitting overdue with a client.</p>
            </div>
          ) : (
            <>
              {/* DECISIONS FIRST */}
              <section className="mt-8" data-testid="agenda-decisions">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Needs a decision from you</h2>
                  <span className="text-xs text-slate-400">({data.left_out.decisions.total})</span>
                </div>
                {data.decisions.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">No decisions are due this week.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {data.decisions.map((it, i) => (
                      <AgendaCard key={`${it.client_id}-${it.type}-${i}`} item={it} index={i}
                        tone={{ badge: "bg-amber-100 text-amber-700", tag: "bg-amber-50 text-amber-700" }} />
                    ))}
                  </div>
                )}
                <LeftOut label="decisions" data={data.left_out.decisions} />
              </section>

              {/* WAITING ON A CLIENT NEXT */}
              <section className="mt-10" data-testid="agenda-awaiting">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Waiting on a client</h2>
                  <span className="text-xs text-slate-400">({data.left_out.awaiting_client.total})</span>
                </div>
                {data.awaiting_client.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">Nothing is sitting with a client right now.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {data.awaiting_client.map((it, i) => (
                      <AgendaCard key={`${it.client_id}-${it.type}-${i}`} item={it} index={i}
                        tone={{ badge: "bg-slate-100 text-slate-600", tag: "bg-slate-100 text-slate-600" }} />
                    ))}
                  </div>
                )}
                <LeftOut label="awaiting" data={data.left_out.awaiting_client} />
              </section>
            </>
          )}

          <p className="mt-8 rounded-lg bg-violet-50 px-4 py-3 text-xs text-violet-800" data-testid="agenda-capture-note">
            {data.capture_note}
          </p>
        </>
      )}
    </div>
  );
}
